import { alpha, Avatar, Box, Typography } from '@mui/material';
import { Ruler } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import AnnotationClearAllButton from '~/components/Annotations/AnnotationClearAllButton';
import { useClearAnnotations } from '~/hooks/useClearAnnotations';
import { placedAnnotationSliceCountState } from '~/state/atoms/measurementAtoms';
import { MeasureMode } from '~/utils/annotationKinds';
import { enqueueSnackbar } from '~/utils/notifications';
import { MEASURE_MODE_ICON } from './MeasureModePicker';

/**
 * The foot of the Measure tray: how to place the armed mode, or how to start
 * measuring, and Clear all of every shape on every slice and image.
 */
export default function MeasureFooter({
    active,
    count,
}: {
    /** The mode armed now; null while another tool is active. */
    active: MeasureMode | null;
    count: number;
}) {
    const intl = useIntl();
    const slices = useRecoilValue(placedAnnotationSliceCountState);
    const clearAnnotations = useClearAnnotations();
    const idle = active === null;
    const Icon = idle ? Ruler : MEASURE_MODE_ICON[active];

    return (
        <Box
            sx={({ palette }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 1,
                bgcolor: alpha(palette.common.black, 0.3),
                borderTop: `1px solid ${palette.divider}`,
                borderRadius: '0 0 7px 7px',
            })}
        >
            <Avatar
                sx={({ palette }) => ({
                    width: 28,
                    height: 28,
                    borderRadius: '6px',
                    color: idle ? 'text.disabled' : 'primary.main',
                    bgcolor: idle
                        ? alpha(palette.common.white, 0.06)
                        : alpha(palette.primary.main, 0.18),
                })}
            >
                <Icon size={16} aria-hidden />
            </Avatar>
            <Box flex={1} minWidth={0}>
                <Typography
                    data-testid="measure-hint"
                    fontSize={12}
                    lineHeight={1.3}
                    color={idle ? 'text.secondary' : 'text.primary'}
                >
                    <FormattedMessage id={`measure.hint.${active ?? 'idle'}`} />
                </Typography>
                {!idle && (
                    <Typography
                        fontSize={11}
                        lineHeight={1.3}
                        color="text.disabled"
                    >
                        <FormattedMessage id="measure.hint.cancel" />
                    </Typography>
                )}
            </Box>
            {count > 0 && (
                <AnnotationClearAllButton
                    itemCount={count}
                    // Said once done: the shapes cleared may lie on slices out
                    // of view.
                    onClear={() => {
                        clearAnnotations();
                        enqueueSnackbar(
                            intl.formatMessage({ id: 'measure.clearAll.done' }),
                            { variant: 'success' }
                        );
                    }}
                    labelId="measure.clearAll"
                    confirmLabelId="measure.clearAll.confirm"
                    tooltip={intl.formatMessage(
                        { id: 'measure.clearAll.tooltip' },
                        { count, slices }
                    )}
                />
            )}
        </Box>
    );
}
